import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { Material, MovimentacaoEstoque } from '../../types';
import {
  CubeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoEstoque[]>([]);
  const [materiaisBaixoEstoque, setMateriaisBaixoEstoque] = useState<Material[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Carregar materiais
      const materiaisRes = await estoqueApi.getMateriais();
      setMateriais(materiaisRes.results || []);

      // Carregar movimentações
      const movimentacoesRes = await estoqueApi.getMovimentacoes({ ordering: '-data', limit: 10 });
      setMovimentacoes(movimentacoesRes.results || []);

      // Carregar materiais com baixo estoque
      const baixoEstoqueRes = await estoqueApi.getMateriaisBaixoEstoque();
      setMateriaisBaixoEstoque(baixoEstoqueRes || []);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalMateriais = materiais.length;
  const valorTotalEstoque = materiais.reduce((sum, mat) => sum + (mat.valor_total_estoque || 0), 0);
  const totalBaixoEstoque = materiaisBaixoEstoque.length;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
    <div className="space-y-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-gray-900">Dashboard de Estoque</h1>
          <p className="text-xs text-gray-600">Visão geral do estoque</p>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {/* Total de Materiais */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">Total de Materiais</p>
              <p className="text-lg font-bold text-blue-700 mt-1">{totalMateriais}</p>
            </div>
            <CubeIcon className="h-6 w-6 text-blue-700 opacity-75" />
          </div>
        </div>

        {/* Valor Total em Estoque */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">Valor Total em Estoque</p>
              <p className="text-lg font-bold text-green-700 mt-1">
                {formatCurrency(valorTotalEstoque)}
              </p>
            </div>
            <CurrencyDollarIcon className="h-6 w-6 text-green-700 opacity-75" />
          </div>
        </div>

        {/* Materiais com Baixo Estoque */}
        <div className={`${totalBaixoEstoque > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">Baixo Estoque</p>
              <p className={`text-lg font-bold ${totalBaixoEstoque > 0 ? 'text-red-700' : 'text-gray-700'} mt-1`}>
                {totalBaixoEstoque}
              </p>
            </div>
            <ExclamationTriangleIcon className={`h-6 w-6 ${totalBaixoEstoque > 0 ? 'text-red-700' : 'text-gray-700'} opacity-75`} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Materiais com Baixo Estoque */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Materiais Críticos</h2>
            <button
              onClick={() => navigate('/estoque/materiais')}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Ver todos
            </button>
          </div>

          {materiaisBaixoEstoque.length === 0 ? (
            <p className="text-gray-500 text-center py-6 text-sm">
              Nenhum material com baixo estoque
            </p>
          ) : (
            <div className="space-y-2">
              {materiaisBaixoEstoque.slice(0, 5).map((material) => (
                <div
                  key={material.id}
                  className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mr-2" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">{material.codigo}</p>
                        <p className="text-xs text-gray-600">{material.nome}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-red-600">
                      {material.saldo_atual} {material.unidade_display}
                    </p>
                    <p className="text-xs text-gray-500">
                      Mín: {material.estoque_minimo}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Últimas Movimentações */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Últimas Movimentações</h2>
            <button
              onClick={() => navigate('/estoque/movimentacoes')}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Ver todas
            </button>
          </div>

          {movimentacoes.length === 0 ? (
            <p className="text-gray-500 text-center py-6 text-sm">
              Nenhuma movimentação registrada
            </p>
          ) : (
            <div className="space-y-2">
              {movimentacoes.slice(0, 5).map((mov) => (
                <div
                  key={mov.id}
                  className="flex items-center justify-between p-2 border rounded hover:bg-gray-50"
                >
                  <div className="flex items-center flex-1">
                    <div className={`p-1.5 rounded-full mr-2 ${
                      mov.tipo === 'E' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <ArrowsRightLeftIcon className={`h-3.5 w-3.5 ${
                        mov.tipo === 'E' ? 'text-green-600' : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900">
                        {mov.material_codigo}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(mov.data)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-semibold ${
                      mov.tipo === 'E' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {mov.tipo === 'E' ? '+' : '-'}{mov.quantidade} {mov.material_unidade}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(mov.valor_total || 0)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Botões de Ação Rápida */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate('/estoque/materiais/novo')}
          className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Novo Material
        </button>
        <button
          onClick={() => navigate('/estoque/movimentacoes/nova')}
          className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Nova Movimentação
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
